/* eslint-disable @next/next/no-img-element */
// HERO "Reserva" (modelo_busca = 8): hero de alto padrão, dark, com muito espaço
// negativo. Fundo = imagem (banner "topo" do tenant) ou vídeo (`site.url_video`),
// coberto por um véu escuro longo (quase-preto → transparente) ancorado em var(--cor1);
// headline serif grande ('Cormorant Garamond'), subtítulo discreto e uma BUSCA EM UMA
// LINHA FINA (transação + cidade + tipo + botão dourado) com link "busca avançada" que
// expande dormitórios + faixa de preço. Acento dourado envelhecido via var(--cor2)
// (fallback #b98a41), com texto escuro legível calculado por ensureReadableTextColor.
// A busca (estado/URL/tracking) vem toda do hook compartilhado useHeroSearch (M2).
// Default export = ErrorBoundary com fallback pro hero padrão (padrão WelcomeIa/Imersivo).
import React, { useMemo, useState } from 'react';
import { ensureReadableTextColor, isColorLight, toPrice } from '../../../utils/FunctionsGlobals';
import { getHeroVideoPoster, getTenantVideoCover } from '../../../utils/heroVideo';
import HeroVideoCover from '../../utils/HeroVideoCover';
import type { HeroSectionProps } from '../../registry/types';
import { useHeroSearch } from '../../shared/hero-search/useHeroSearch';
import CampoSelecao from '../../shared/hero-search/CampoSelecao';
import CardsBusca from '../../shared/cards-busca/CardsBusca';
import DrawerComponent from '../../drawer/DrawerComponent';
import styles from './WelcomeReserva.module.css';
import { usePreviaBuilder } from '../../../lib/previaBuilder';

// Contrato = HeroSectionProps do registry (superset transcrito das props reais dos
// switches). É o que o lazySection<HeroSectionProps> exige para entrar no mapa NEW_HERO.

const stripHtml = (value: unknown): string =>
    String(value ?? '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const WelcomeReservaInner: React.FC<HeroSectionProps> = ({
    allCities,
    allTypes,
    allTransacao,
    site,
    urlBanner,
    origin,
    codsite2,
    setActiveDrawer,
    activeDrawer,
    favorito,
    isMobile1,
    props,
    allCards,
    valores,
}) => {
    const { videoParado } = usePreviaBuilder(); // prévia dos cards do CRM: vídeo parado na capa
    const {
        transacoes,
        transacao,
        setTransacao,
        cidade,
        setCidade,
        tipo,
        setTipo,
        dormitorios,
        setDormitorios,
        precoMin,
        setPrecoMin,
        precoMax,
        setPrecoMax,
        loading,
        onSubmit,
    } = useHeroSearch({ site, allCities, allTypes, allTransacao, codsite2, origin });

    const [expandido, setExpandido] = useState(false);

    // cs2422 (toc2imoveis): o cliente pediu a FAIXA DE PREÇO na linha da busca, no lugar do
    // campo Cidade — ele anuncia numa praça só, e quem chega na home procura por valor, não
    // por cidade. Gate por tenant (mesmo padrão do preço digitável do cs2551): os outros 10
    // sites com modelo_busca=8 seguem byte a byte como estão.
    // A Cidade não some do hero: desce para a "busca avançada", que é de onde o preço saiu.
    const precoNaLinha = Number(site?.id) === 2422;

    // cs2617 (jocaciaimobiliaria): "Valor mínimo" e "Valor máximo" como DOIS campos com rótulo
    // próprio na linha da busca, depois do Tipo — o desenho do hero Autoral (modelo 11) — mantendo
    // Transação/Cidade/Tipo como estão. Só esta licença; os outros 11 tenants do modelo 8 não mudam.
    const precoLadoALado = Number(site?.id) === 2617;

    // A faixa REAL do tenant (a mesma que a listagem usa) vira o placeholder dos dois campos.
    // Sem ela o visitante não tem como saber que este site só anuncia LOCAÇÃO — digitava valor de
    // venda, a busca não achava nada na faixa e a listagem devolvia o acervo inteiro (29/08).
    const faixaDoTenant = useMemo(() => {
        const bruto =
            String(transacao).toLowerCase() === 'venda'
                ? valores?.precos_venda?.[0]
                : valores?.precos_locacao?.[0];
        const min = Number(bruto?.min);
        const max = Number(bruto?.max);
        if (!Number.isFinite(min) || !Number.isFinite(max) || max <= 0) return null;
        return { min, max };
    }, [valores, transacao]);

    const moeda = site?.moeda_tipo || 'R$';
    const rotuloFaixa = (extremo: 'min' | 'max') => {
        const padrao = extremo === 'min' ? 'Mínimo' : 'Máximo';
        if (!faixaDoTenant) return padrao;
        return `${extremo === 'min' ? 'Mín.' : 'Máx.'} ${moeda} ${toPrice(faixaDoTenant[extremo], 0)}`;
    };
    // Nos campos com rótulo próprio (cs2617) o "Mínimo"/"Máximo" repetiria o rótulo: sem faixa
    // conhecida, entra o "R$ 0,00" do modelo 11.
    const placeholderValor = (extremo: 'min' | 'max') =>
        faixaDoTenant ? rotuloFaixa(extremo) : `${moeda} 0,00`;

    // O campo guarda só DÍGITOS e mostra o número pontuado. Era <input type="number">: digitar
    // "1.500" (o jeito brasileiro) deixava o campo inválido para o próprio navegador e o submit
    // do form era engolido — o botão BUSCAR simplesmente não fazia nada.
    const soDigitos = (valor: string) => String(valor ?? '').replace(/\D/g, '');
    const comPontuacao = (digitos: string) =>
        digitos ? toPrice(Number(digitos), 0) : '';

    // TODOS os campos da linha usam o MESMO campo (CampoSelecao): clica e vê a lista inteira,
    // digita e filtra. O <select> nativo ficou para trás porque o dropdown dele é desenhado pelo
    // sistema e não aceita CSS — misturado com um combobox na mesma linha, destoava.
    // O valor que cada campo entrega ao useHeroSearch é o mesmo de antes, então a URL não muda.
    const opcoesTransacao = useMemo(
        () => transacoes.map((t) => ({ label: t.label, value: String(t.value).toLowerCase() })),
        [transacoes]
    );

    // Acento dourado: a paleta do catálogo controla via --cor2; #b98a41 é o fallback.
    // O texto do botão é CALCULADO p/ garantir contraste sobre o acento (padrão WelcomeSplit).
    const accent = site?.cor2 || '#b98a41';
    const accentText =
        ensureReadableTextColor(isColorLight(accent) ? site?.cor11 : site?.cor13, accent) || '#1a1a1a';

    // Mídia de fundo: vídeo do tenant (fora do mobile, p/ poupar dados) OU banner "topo".
    const bannerSrc = urlBanner && urlBanner !== 'undefined' ? urlBanner.replaceAll('"', '') : '';
    const videoSrc =
        site?.url_video && !isMobile1 ? String(site.url_video).replaceAll('"', '') : '';
    const heroPoster = getHeroVideoPoster(site?.url_video);
    // No celular o vídeo continua desligado, mas a CAPA dele entra no lugar do fundo liso
    // (#1271): pôster do vídeo regional > pôster do vídeo padrão > capa do vídeo do tenant.
    const capaVideo = site?.url_video_poster || heroPoster || getTenantVideoCover(site?.url_video);

    const headline = site?.frase_busca ? stripHtml(site.frase_busca) : 'O endereço da sua próxima história';
    const subtitle = site?.frase_busca_2
        ? stripHtml(site.frase_busca_2)
        : 'Uma seleção de imóveis para quem busca exclusividade, discrição e bom gosto.';

    // cs2465 (#1109): o `.headline` fixa 'Cormorant Garamond' no CSS module, então o título do
    // banner é o ÚNICO texto do site que não acompanha a fonte que o tenant escolheu no
    // Personalizar Site. O 2465 já publicou a --fonte-principal (Archivo) e pediu o título na
    // MESMA fonte, por legibilidade. Guard por tenant de propósito: os outros sites do hero
    // "Reserva" seguem com a Cormorant, que é a identidade do modelo — nenhum deles pediu troca.
    const estiloTitulo = site?.id == 2465 ? { fontFamily: 'var(--fonte-principal)' } : undefined;

    const dormOptions = [1, 2, 3, 4, 5].map((d) => ({
        label: d === 5 ? '5 ou mais' : `${d}+`,
        value: String(d),
    }));

    return (
        <section className={styles.reserva} data-builder-section="busca">
            <DrawerComponent
                site={site}
                activeDrawer={activeDrawer}
                setActiveDrawer={setActiveDrawer}
                favorito={favorito || []}
                menus={props?.menus}
            />

            {/* ---------- mídia de fundo + véu ---------- */}
            <div className={styles.media} aria-hidden="true">
                {videoSrc ? (
                    <video
                        className={styles.mediaEl}
                        autoPlay={!videoParado}
                        muted
                        loop
                        playsInline
                        preload="none"
                        poster={heroPoster}
                        src={videoSrc}
                    >
                        <track kind="captions" label="Legenda" src="legenda.vtt" />
                    </video>
                ) : bannerSrc ? (
                    <img className={styles.mediaEl} src={bannerSrc} alt="" />
                ) : (
                    <HeroVideoCover
                        src={capaVideo}
                        className={styles.mediaEl}
                        fallbackClassName={styles.mediaFallback}
                    />
                )}
                <div className={styles.tint} />
                <div className={styles.veil} />
            </div>

            {/* ---------- conteúdo ---------- */}
            <div className={styles.content}>
                <span className={styles.eyebrow}>Seleção exclusiva</span>
                <h1 className={styles.headline} style={estiloTitulo}>{headline}</h1>
                <p className={styles.subtitle}>{subtitle}</p>

                {/* id="myLink": âncora do botão "BUSCA AVANÇADA" do topo (topo2). */}
                <form
                    id="myLink"
                    className={`${styles.searchWrap} ${precoLadoALado ? styles.searchWrapLarga : ''}`}
                    onSubmit={onSubmit}
                >
                    <div className={styles.searchLine}>
                        {/* div, não label: o painel de sugestões mora dentro do campo e um
                            <label> em volta de um listbox rotularia a lista inteira. */}
                        <div className={styles.searchField}>
                            <span className={styles.fieldLabel}>Transação</span>
                            <CampoSelecao
                                opcoes={opcoesTransacao}
                                valor={transacao}
                                onChange={setTransacao}
                                ariaLabel="Transação"
                                className={styles.campoBusca}
                            />
                        </div>

                        <span className={styles.divider} aria-hidden="true" />

                        {precoNaLinha ? (
                            /* Um único slot (o mesmo que a Cidade ocupava) com os dois extremos
                               lado a lado: no mobile a linha vira coluna, e dois slots separados
                               virariam duas faixas soltas de "Preço". */
                            <div className={styles.searchField}>
                                <span className={styles.fieldLabel}>
                                    Preço ({site?.moeda_tipo || 'R$'})
                                </span>
                                <div className={styles.precoPar}>
                                    <input
                                        className={`${styles.input} ${styles.precoInput}`}
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="off"
                                        placeholder={rotuloFaixa('min')}
                                        value={comPontuacao(precoMin)}
                                        onChange={(e) => setPrecoMin(soDigitos(e.target.value))}
                                        aria-label="Preço mínimo"
                                    />
                                    <span className={styles.precoSep} aria-hidden="true">–</span>
                                    <input
                                        className={`${styles.input} ${styles.precoInput}`}
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="off"
                                        placeholder={rotuloFaixa('max')}
                                        value={comPontuacao(precoMax)}
                                        onChange={(e) => setPrecoMax(soDigitos(e.target.value))}
                                        aria-label="Preço máximo"
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className={styles.searchField}>
                                <span className={styles.fieldLabel}>Cidade</span>
                                <CampoSelecao
                                    opcoes={allCities || []}
                                    valor={cidade}
                                    onChange={setCidade}
                                    rotuloVazio="Todas as cidades"
                                    ariaLabel="Cidade"
                                    className={styles.campoBusca}
                                />
                            </div>
                        )}

                        <span className={styles.divider} aria-hidden="true" />

                        <div className={styles.searchField}>
                            <span className={styles.fieldLabel}>Tipo</span>
                            <CampoSelecao
                                opcoes={allTypes || []}
                                valor={tipo}
                                onChange={setTipo}
                                rotuloVazio="Todos os tipos"
                                ariaLabel="Tipo de imóvel"
                                className={styles.campoBusca}
                            />
                        </div>

                        {precoLadoALado ? (
                            /* cs2617: dois campos com rótulo próprio (Valor mínimo | Valor máximo),
                               no desenho do hero Autoral. <label> aqui pode envolver o input: não há
                               listbox dentro, ao contrário dos CampoSelecao. */
                            <>
                                <span className={styles.divider} aria-hidden="true" />
                                <div className={styles.precoDupla}>
                                    <label className={styles.searchField}>
                                        <span className={styles.fieldLabel}>Valor mínimo</span>
                                        <input
                                            className={`${styles.input} ${styles.precoInput}`}
                                            type="text"
                                            inputMode="numeric"
                                            autoComplete="off"
                                            placeholder={placeholderValor('min')}
                                            value={comPontuacao(precoMin)}
                                            onChange={(e) => setPrecoMin(soDigitos(e.target.value))}
                                            aria-label="Valor mínimo"
                                        />
                                    </label>
                                    <span className={styles.divider} aria-hidden="true" />
                                    <label className={styles.searchField}>
                                        <span className={styles.fieldLabel}>Valor máximo</span>
                                        <input
                                            className={`${styles.input} ${styles.precoInput}`}
                                            type="text"
                                            inputMode="numeric"
                                            autoComplete="off"
                                            placeholder={placeholderValor('max')}
                                            value={comPontuacao(precoMax)}
                                            onChange={(e) => setPrecoMax(soDigitos(e.target.value))}
                                            aria-label="Valor máximo"
                                        />
                                    </label>
                                </div>
                            </>
                        ) : null}

                        <button
                            type="submit"
                            className={styles.submit}
                            style={{ background: accent, color: accentText }}
                            disabled={loading}
                            aria-label="Buscar imóveis"
                        >
                            {loading ? 'Buscando...' : 'Buscar'}
                        </button>
                    </div>

                    {expandido ? (
                        <div className={styles.advanced}>
                            <div className={styles.advField}>
                                <span className={styles.fieldLabel}>Dormitórios</span>
                                <CampoSelecao
                                    opcoes={dormOptions}
                                    valor={dormitorios}
                                    onChange={setDormitorios}
                                    rotuloVazio="Indiferente"
                                    ariaLabel="Dormitórios"
                                    className={styles.campoBusca}
                                />
                            </div>
                            {precoNaLinha ? (
                                /* O preço subiu para a linha; a Cidade toma o lugar dele aqui,
                                   para o filtro continuar ao alcance de quem procura por ela. */
                                <div className={styles.advField}>
                                    <span className={styles.fieldLabel}>Cidade</span>
                                    <CampoSelecao
                                        opcoes={allCities || []}
                                        valor={cidade}
                                        onChange={setCidade}
                                        rotuloVazio="Todas as cidades"
                                        ariaLabel="Cidade"
                                        className={styles.campoBusca}
                                    />
                                </div>
                            ) : precoLadoALado ? null : (
                                /* cs2617: o preço já está na linha; aqui fica só Dormitórios. */
                                <>
                                    <label className={styles.advField}>
                                        <span className={styles.fieldLabel}>Preço mínimo ({site?.moeda_tipo || 'R$'})</span>
                                        <input
                                            className={styles.input}
                                            type="number"
                                            inputMode="numeric"
                                            min={0}
                                            placeholder="Mínimo"
                                            value={precoMin}
                                            onChange={(e) => setPrecoMin(e.target.value)}
                                            aria-label="Preço mínimo"
                                        />
                                    </label>
                                    <label className={styles.advField}>
                                        <span className={styles.fieldLabel}>Preço máximo ({site?.moeda_tipo || 'R$'})</span>
                                        <input
                                            className={styles.input}
                                            type="number"
                                            inputMode="numeric"
                                            min={0}
                                            placeholder="Máximo"
                                            value={precoMax}
                                            onChange={(e) => setPrecoMax(e.target.value)}
                                            aria-label="Preço máximo"
                                        />
                                    </label>
                                </>
                            )}
                        </div>
                    ) : null}

                    <button
                        type="button"
                        className={styles.advancedToggle}
                        aria-expanded={expandido}
                        onClick={() => setExpandido((v) => !v)}
                    >
                        {expandido ? 'Ocultar busca avançada' : 'Busca avançada'}
                    </button>
                </form>

                <CardsBusca cards={allCards} site={site} variante="escuro" />
            </div>
        </section>
    );
};

class WelcomeReserva extends React.Component<HeroSectionProps, { hasError: boolean }> {
    constructor(props: HeroSectionProps) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch() {
        // silencioso: degrada pro fallback sem quebrar a home.
    }
    render() {
        const { fallback, site } = this.props;
        if (this.state.hasError || !site) return fallback ?? null;
        return <WelcomeReservaInner {...this.props} />;
    }
}

export default WelcomeReserva;
